import React from 'react';
import './TrendingIndicators.css';

/**
 * TrendingIndicators Component
 * Displays trending badges and indicators for tokens
 */
const TrendingIndicators = ({ 
  token, 
  trendingData = null, 
  showScore = false,
  size = 'medium' 
}) => {
  if (!trendingData && !token.trending) {
    return null;
  }

  const trending = trendingData || token.trendingData || {};
  const trendScore = trending.trend_score || trending.trendScore || 0;
  const volumeSpike = trending.volume_spike_24h || trending.volumeSpike24h || 0;
  const priceMovement = trending.price_momentum_1h || trending.priceMovement1h || 0;
  const socialScore = trending.social_activity_score || trending.socialScore || 0;

  const getTrendLevel = (score) => {
    if (score >= 80) return 'hot';
    if (score >= 60) return 'trending';
    if (score >= 40) return 'rising';
    return 'normal';
  };

  const trendLevel = getTrendLevel(trendScore);

  const indicators = [];

  // Main trending indicator
  if (trendScore >= 40) {
    indicators.push({
      type: 'trending',
      level: trendLevel,
      icon: trendLevel === 'hot' ? '🔥' : trendLevel === 'trending' ? '⭐' : '📈',
      label: trendLevel === 'hot' ? 'HOT' : trendLevel === 'trending' ? 'TRENDING' : 'RISING',
      score: trendScore
    });
  }

  // Volume spike indicator
  if (volumeSpike >= 200) {
    indicators.push({
      type: 'volume',
      level: volumeSpike >= 500 ? 'extreme' : 'high',
      icon: '📊',
      label: `+${Math.round(volumeSpike)}% VOL`,
      score: volumeSpike
    });
  }

  // Price movement indicator
  if (Math.abs(priceMovement) >= 25) {
    indicators.push({
      type: 'price',
      level: priceMovement > 0 ? 'up' : 'down',
      icon: priceMovement > 0 ? '🚀' : '📉',
      label: `${priceMovement > 0 ? '+' : ''}${Math.round(priceMovement)}%`,
      score: Math.abs(priceMovement)
    });
  }

  // Social activity indicator
  if (socialScore >= 60) {
    indicators.push({
      type: 'social',
      level: socialScore >= 80 ? 'viral' : 'active',
      icon: '💬',
      label: 'BUZZ',
      score: socialScore
    });
  }

  if (indicators.length === 0) {
    return null;
  }

  return (
    <div className={`trending-indicators ${size}`}>
      {indicators.map((indicator, index) => (
        <div
          key={`${indicator.type}-${index}`}
          className={`trending-badge ${indicator.type} ${indicator.level}`}
          title={`${indicator.label} - Score: ${Math.round(indicator.score)}`}
        >
          <span className="badge-icon">{indicator.icon}</span>
          <span className="badge-label">{indicator.label}</span>
          {showScore && (
            <span className="badge-score">{Math.round(indicator.score)}</span>
          )}
        </div>
      ))}
      
      {showScore && trendScore > 0 && (
        <div className={`trend-score ${trendLevel}`}>
          <span className="score-label">Trend Score:</span>
          <span className="score-value">{Math.round(trendScore)}</span>
        </div>
      )}
    </div>
  );
};

/**
 * TrendingBadge Component
 * Simple trending badge for compact display
 */
export const TrendingBadge = ({ trendScore, compact = false }) => {
  if (!trendScore || trendScore < 40) {
    return null;
  }

  const getTrendLevel = (score) => {
    if (score >= 80) return { level: 'hot', icon: '🔥', label: 'HOT' };
    if (score >= 60) return { level: 'trending', icon: '⭐', label: 'TRENDING' };
    return { level: 'rising', icon: '📈', label: 'RISING' };
  };

  const trend = getTrendLevel(trendScore);

  return (
    <div className={`trending-badge-simple ${trend.level} ${compact ? 'compact' : ''}`}>
      <span className="badge-icon">{trend.icon}</span>
      {!compact && <span className="badge-label">{trend.label}</span>}
    </div>
  );
};

/**
 * TrendingTooltip Component
 * Detailed trending information in tooltip format
 */
export const TrendingTooltip = ({ trendingData }) => {
  if (!trendingData) {
    return null;
  }

  const {
    trend_score: trendScore = 0,
    volume_spike_24h: volumeSpike = 0,
    price_momentum_1h: priceMovement = 0,
    social_activity_score: socialScore = 0,
    liquidity_score: liquidityScore = 0,
    detection_source: source = 'unknown',
    first_detected_at: detectedAt
  } = trendingData;

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return `${Math.floor(diffMinutes / 1440)}d ago`;
  };

  return (
    <div className="trending-tooltip">
      <div className="tooltip-header">
        <h4>Trending Analysis</h4>
        <div className="trend-score-display">
          <span className="score-value">{Math.round(trendScore)}</span>
          <span className="score-max">/100</span>
        </div>
      </div>
      
      <div className="tooltip-metrics">
        <div className="metric-row">
          <span className="metric-label">Volume Spike:</span>
          <span className="metric-value">+{Math.round(volumeSpike)}%</span>
        </div>
        
        <div className="metric-row">
          <span className="metric-label">Price Momentum:</span>
          <span className={`metric-value ${priceMovement >= 0 ? 'positive' : 'negative'}`}>
            {priceMovement >= 0 ? '+' : ''}{Math.round(priceMovement)}%
          </span>
        </div>
        
        <div className="metric-row">
          <span className="metric-label">Social Activity:</span>
          <span className="metric-value">{Math.round(socialScore)}/100</span>
        </div>
        
        <div className="metric-row">
          <span className="metric-label">Liquidity Score:</span>
          <span className="metric-value">{Math.round(liquidityScore)}/100</span>
        </div>
      </div>
      
      <div className="tooltip-footer">
        <div className="detection-info">
          <span className="detection-source">Source: {source}</span>
          <span className="detection-time">Detected {formatTime(detectedAt)}</span>
        </div>
      </div>
    </div>
  );
};

export default TrendingIndicators;